/**
 * Detects non-actionable errors that originate entirely from browser
 * extensions. These are errors where every stack frame points at a
 * `chrome-extension://`, `moz-extension://`, or `safari-web-extension://`
 * URL, meaning no application code is involved.
 *
 * Example: a Chrome extension running `querySelector` with an invalid CSS
 * selector produces a `SyntaxError` whose stack is entirely within the
 * extension bundle. The Datadog RUM SDK captures this as an unhandled
 * error, but it is not actionable from application code.
 *
 * Extension code that throws inside a native builtin also carries the
 * builtin's own frame, which has no source URL. A wallet extension (here
 * Phantom's `evmAsk.js`) calling `Object.defineProperty(window, "ethereum")`
 * after another wallet already locked that property throws
 * `TypeError: Cannot redefine property: ethereum` with this stack:
 *
 *   at Object.defineProperty @ <anonymous>
 *   at t.inject @ chrome-extension://<id>/evmAsk.js:76:5093
 *
 * Such sourceless builtin frames (`@ <anonymous>`, `@ [native code]`) are
 * skipped, but at least one extension frame is still required, so a stack
 * made only of builtin frames is not treated as extension noise.
 */

const EXTENSION_SCHEMES = [
  "chrome-extension://",
  "moz-extension://",
  "safari-web-extension://",
];

/** A builtin frame with no source location, as the RUM SDK prints it. */
const SOURCELESS_BUILTIN_FRAME_PATTERN = / @ (?:<anonymous>|\[native code\])$/;

export function isBrowserExtensionStackNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message || !stack) {
    return false;
  }

  const frames = stack
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("at "))
    .filter((frame) => !SOURCELESS_BUILTIN_FRAME_PATTERN.test(frame));

  if (frames.length === 0) {
    return false;
  }

  return frames.every((frame) =>
    EXTENSION_SCHEMES.some((scheme) => frame.includes(scheme)),
  );
}
